import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css'; // reuse same styles

function About() {
  return (
    <div className="privacy-policy-page">
      <div className="privacy-header">
        <Link to="/login" className="privacy-back-link">← Back</Link>
        <h1>
          <img src="/belong.png" alt="Belong" style={{ height: '48px', verticalAlign: 'middle', marginRight: '8px' }} />
          About
        </h1>
        <p className="privacy-subtitle">Find where you belong, safely</p>
      </div>

      <div className="privacy-content">
        <section className="privacy-section">
          <h2>Our Mission</h2>
          <p>
            Millions of people carry invisible identities that shape their lives but remain hidden — 
            caste, mental health, neurodiversity, chronic illness, socioeconomic background. 
            These identities are deeply personal, often stigmatized, and rarely discussed openly.
          </p>
          <p>
            Belong exists to change that. We built a privacy-first platform where people with 
            invisible identities can find others who share their experience, build genuine 
            connections, and feel less alone — all without compromising their safety.
          </p>
        </section>

        <section className="privacy-section privacy-highlight">
          <h2>Why We Built This</h2>
          <p className="golden-rule">
            A safe space for people with invisible identities to find where they belong.
          </p>
          <p style={{ textAlign: 'center', marginTop: '8px' }}>
            No other platform is designed specifically for identities that aren't always visible — 
            the ones you carry every day but can't always talk about. 
            We built Belong to fill that gap — with privacy as the foundation, not an afterthought.
          </p>
        </section>

        <section className="privacy-section">
          <h2>What Makes Belong Different</h2>
          <div className="disclosure-levels">
            <div className="disclosure-level">
              <span className="level-badge" style={{ backgroundColor: '#7C3AED' }}>🔒</span>
              <span>Privacy by architecture — your data is encrypted, not just protected by policy</span>
            </div>
            <div className="disclosure-level">
              <span className="level-badge" style={{ backgroundColor: '#14B8A6' }}>🤝</span>
              <span>Mutual consent at every step — both people agree before anything is shared</span>
            </div>
            <div className="disclosure-level">
              <span className="level-badge" style={{ backgroundColor: '#F97316' }}>🧩</span>
              <span>Multiple identities welcome — because identity is complex and intersectional</span>
            </div>
            <div className="disclosure-level">
              <span className="level-badge" style={{ backgroundColor: '#10B981' }}>👁️</span>
              <span>Identities never revealed unless shared — enforced at the database level</span>
            </div>
          </div>
        </section>

        <section className="privacy-section">
          <h2>Our Approach to Privacy</h2>
          <p>
            We don't just say we care about privacy — we engineered it into every layer:
          </p>
          <ul>
            <li>Personal data (names, phone, email) is encrypted with AES-256-GCM with versioned key rotation</li>
            <li>Location matching uses one-way hashes — your city name isn't stored in readable form</li>
            <li>Messages are encrypted before storage</li>
            <li>IP addresses are hashed in audit logs — we can't trace them back to you</li>
            <li>Progressive disclosure gives you control over what you share and when</li>
            <li>No advertising, no data selling, no tracking cookies</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Who Built This</h2>
          <p>
            Belong was built by technology leaders who spent over two decades designing 
            and scaling platforms at Amazon and PayPal — systems used by hundreds of millions 
            of people worldwide.
          </p>
          <p>
            That experience includes building multi-tenant enterprise platforms, self-service 
            systems that saved hundreds of millions in costs, personalization engines driving 
            billions in revenue, and security architectures protecting sensitive financial data 
            at scale.
          </p>
          <p>
            We bring that same rigor — in security, scalability, and customer-first design — 
            to protecting something even more personal than financial data: your identity.
          </p>
          <p>
            The same engineering standards that protect your money at PayPal and your purchases 
            at Amazon now protect who you are at Belong.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Our Values</h2>
          <div className="third-party-list">
            <div className="third-party-item">
              <span className="tp-name">Privacy First, Always</span>
              <span className="tp-purpose">We'd rather lose a feature than compromise your privacy</span>
            </div>
            <div className="third-party-item">
              <span className="tp-name">You Own Your Data</span>
              <span className="tp-purpose">Export it, delete it, control who sees it — it's yours</span>
            </div>
            <div className="third-party-item">
              <span className="tp-name">Trust is Earned</span>
              <span className="tp-purpose">Progressive disclosure means trust is built gradually, not assumed</span>
            </div>
            <div className="third-party-item">
              <span className="tp-name">Identity is Complex</span>
              <span className="tp-purpose">You're not one label. Multiple identities, one community</span>
            </div>
            <div className="third-party-item">
              <span className="tp-name">Free for Everyone</span>
              <span className="tp-purpose">Belonging shouldn't have a price tag</span>
            </div>
          </div>
        </section>

        <section className="privacy-section">
          <h2>Get In Touch</h2>
          <ul>
            <li><Link to="/register">Join Belong</Link> — find your community</li>
            <li><Link to="/help">Contact Us</Link> — questions, feedback, or partnership ideas</li>
            <li>Email: <a href="mailto:belong@ourbelong.com" style={{ color: '#7C3AED' }}>belong@ourbelong.com</a></li>
          </ul>
        </section>

        <div className="privacy-footer">
          <p>💜 A safe space for people with invisible identities to find where they belong.</p>
        </div>
      </div>
    </div>
  );
}

export default About;
